import { Dialog } from '@headlessui/react';
import { useEffect, useState } from 'react';

import { showSuccessToast } from '@/utils/toasts/Toasts';
import { trpcReact } from '@/utils/trpc';

interface AuthenticatorModalProps {
  isAuthenticatorModalOpen: boolean;
  setIsAuthenticatorModalOpen: (val: boolean) => void;
}

export const AuthenticatorModal = ({
  isAuthenticatorModalOpen,
  setIsAuthenticatorModalOpen,
}: AuthenticatorModalProps) => {
  const [useMfa, setUseMfa] = useState(false);

  // Fetch user metadata
  const { data: profileData } = trpcReact.user.getAuth0Profile.useQuery(undefined, {
    enabled: isAuthenticatorModalOpen,
  });

  // Update metadata mutation
  const updateMetadataMutation = trpcReact.user.updateUserMetadata.useMutation();

  // Set initial MFA state when profile data is loaded
  useEffect(() => {
    if (profileData?.user_metadata?.use_mfa !== undefined) {
      setUseMfa(profileData.user_metadata.use_mfa);
    }
  }, [profileData]);

  // Handle toggle change
  const handleMfaToggle = async () => {
    const newMfaState = !useMfa;
    setUseMfa(newMfaState);

    try {
      await updateMetadataMutation.mutateAsync({
        user_metadata: {
          ...profileData?.user_metadata,
          use_mfa: newMfaState,
        },
      });
      showSuccessToast(newMfaState ? '2FA enabled at next login' : '2FA disabled');
    } catch (error) {
      // Revert state if update fails
      setUseMfa(!newMfaState);
      // Log error to monitoring service in production
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to update MFA settings:', error);
      }
    }
  };

  return (
    <Dialog
      open={isAuthenticatorModalOpen}
      onClose={() => setIsAuthenticatorModalOpen(false)}
      className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50"
      data-testid="authenticator-modal"
    >
      <Dialog.Panel className="flex h-fit w-[454px] flex-col items-center justify-center rounded-[5px] border-2 border-dark-border bg-container-dark p-4">
        <Dialog.Title className="w-[355px] text-center text-[25px] font-bold text-marveri-white">
          Two-Factor Authentication
        </Dialog.Title>

        <div className="mt-4 flex w-full items-center justify-between px-6">
          <span className="text-marveri-white">Enable Two-Factor Authentication</span>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={useMfa}
              onChange={handleMfaToggle}
              disabled={updateMetadataMutation.isLoading}
            />
            <div className="peer h-6 w-11 rounded-full bg-container-darkest after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-marveri-silver after:bg-white after:transition-all after:content-[''] peer-checked:bg-marveri-gold peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
          </label>
        </div>

        {updateMetadataMutation.isError && (
          <p className="mt-2 text-sm text-red-500">
            Failed to update MFA settings. Please try again.
          </p>
        )}

        <div className="m-6 flex w-4/5 justify-evenly">
          <button
            onClick={() => setIsAuthenticatorModalOpen(false)}
            className="h-[38px] w-[100px] rounded border border-dark-border bg-container-darkest text-marveri-muted-silver hover:bg-container-hover"
          >
            Close
          </button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
